import { useTrail, animated } from '@react-spring/web';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { getImageAtSize } from '../../lib/utils';

import styles from '../../../styles/icons/multi-projectron.module.css';

const initialItems = [
    'translate(-314.9) scale(1.5)',
    'translate(-299.2 49.7) scale(1.4)',
    'translate(-284.2 96.8) scale(1.3)',
    'translate(-270 141.6) scale(1.3)',
    'translate(-256.5 184.2) scale(1.2)',
    'translate(-243.7 224.7) scale(1.1)',
];

const propTypes = {
    open: PropTypes.bool,
    image: PropTypes.shape({}),
    items: PropTypes.arrayOf(PropTypes.string),
    onRest: PropTypes.func,
    className: PropTypes.string,
};

const Trail = ({ open, items, url, onRest }) => {
    const trail = useTrail(items.length, {
        config: { mass: 10, tension: 1200, friction: 200 },
        opacity: open ? 1 : 0,
        x: open ? 0 : 20,
        y: open ? 0 : 20,
        from: { opacity: 0.2, x: 20, y: 20 },
        onRest: () => {
            if (onRest !== null) {
                onRest(open);
            }
        },
        // trail: 1000,
    });

    return trail.map((style, index) => (
        <animated.g key={`trail_${index + 1}`} clipPath={`url(#clippath-${index})`} style={style}>
            <use
                transform={items[items]}
                href={`#${url || 'image'}`}
                xlinkHref={`#${url || 'image'}`}
            />
        </animated.g>
    ));
};

const MultiProjectron = ({
    open = true,
    items = initialItems,
    image = null,
    onRest = null,
    className = null,
}) => {
    const {
        url = null,
        width = null,
        height = null,
    } = useMemo(() => getImageAtSize(image || {}, 'huge'), [image]);

    return (
        <svg
            width="1477.8"
            height="993.1"
            viewBox="0 0 1477.8 993.1"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={classNames([styles.container, { [className]: className !== null }])}
        >
            <defs>
                <clipPath className={styles.clipPath} id="clippath-0">
                    <path
                        fill="none"
                        d="M1379.6,0c-14.8,0-28.9,3.3-41.5,9.2L36.9,501.2c-21.6,8.6-36.9,29.6-36.9,54.3v379.1c0,32.3,26.2,58.4,58.4,58.4h886.2c22.3,0,41.6-12.5,51.5-30.8l468.8-815.7c8.2-14.3,12.8-30.8,12.8-48.5,0-54.2-43.9-98.1-98.1-98.1Z"
                    />
                </clipPath>
                <clipPath className={styles.clipPath} id="clippath-1">
                    <path
                        fill="none"
                        d="M1310.6,49.7c-14.1,0-27.4,3.1-39.4,8.7L35.1,525.8c-20.5,8.1-35.1,28.1-35.1,51.6v360.2c0,30.7,24.9,55.5,55.5,55.5h841.9c21.2,0,39.5-11.8,48.9-29.3l445.3-774.9c7.7-13.6,12.2-29.3,12.2-46.1,0-51.5-41.7-93.2-93.2-93.2Z"
                    />
                </clipPath>
                <clipPath className={styles.clipPath} id="clippath-2">
                    <path
                        fill="none"
                        d="M1245.1,96.8c-13.4,0-26.1,3-37.4,8.3L33.3,549.2c-19.5,7.7-33.3,26.7-33.3,49v342.2c0,29.1,23.6,52.7,52.7,52.7h799.8c20.1,0,37.6-11.2,46.5-27.8l423.1-736.1c7.4-12.9,11.6-27.8,11.6-43.8,0-48.9-39.6-88.6-88.6-88.6Z"
                    />
                </clipPath>
                <clipPath className={styles.clipPath} id="clippath-3">
                    <path
                        fill="none"
                        d="M1182.9,141.6c-12.7,0-24.8,2.8-35.6,7.9L31.7,571.4c-18.5,7.3-31.7,25.4-31.7,46.5v325.1c0,27.7,22.4,50.1,50.1,50.1h759.8c19.1,0,35.7-10.7,44.1-26.4l401.9-699.3c7-12.3,11-26.4,11-41.6,0-46.5-37.7-84.1-84.1-84.1Z"
                    />
                </clipPath>
                <clipPath className={styles.clipPath} id="clippath-4">
                    <path
                        fill="none"
                        d="M1123.7,184.2c-12.1,0-23.5,2.7-33.8,7.5L30.1,592.5c-17.6,7-30.1,24.1-30.1,44.2v308.8c0,26.3,21.3,47.6,47.6,47.6h721.8c18.1,0,33.9-10.2,41.9-25.1l381.8-664.4c6.6-11.7,10.5-25.1,10.5-39.5,0-44.1-35.8-79.9-79.9-79.9Z"
                    />
                </clipPath>
                <clipPath className={styles.clipPath} id="clippath-5">
                    <path
                        fill="none"
                        d="M1067.5,224.7c-11.5,0-22.3,2.6-32.1,7.1L28.6,612.5c-16.7,6.6-28.6,22.9-28.6,42v293.4c0,25,20.2,45.2,45.2,45.2h685.7c17.2,0,32.2-9.6,39.8-23.8l362.7-631.2c6.3-11.1,9.9-23.9,9.9-37.5,0-41.9-34-75.9-75.9-75.9Z"
                    />
                </clipPath>
                <image
                    id={url || 'image'}
                    width={width || null}
                    height={height || null}
                    href={url || null}
                    xlinkHref={url || null}
                />
            </defs>
            <Trail open={open} items={items} onRest={onRest} url={url} />
        </svg>
    );
};

MultiProjectron.propTypes = propTypes;

export default MultiProjectron;
